import { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  hideAdGroupDeviceEdit,
  hidePreview,
  setDeviceSettingType,
  setDeviceTypes,
  showPreview,
  updateAdGroupsDevice,
} from 'features/dashboard/operation/adGroupDeviceEdit/adGroupDeviceEditSlice'
import { Modal } from 'components/dialog/Modals'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import { isEmpty, isEmptyObject, isSetEqual } from 'utils'
import Table from 'components/table/Table'
import RadioButton from 'components/form/RadioButton'
import CheckBox from 'components/form/CheckBox'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import Loading from 'components/loading/Loading'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import DeviceName from './DeviceName'
import FixedTable from 'components/table/FixedTable'

export const deviceEquals = (device, other) => {
  if (device.type === 'ALL' && other.type === 'ALL') {
    return true
  } else {
    return (
      device.type === other.type &&
      isSetEqual(device.deviceTypes, other.deviceTypes)
    )
  }
}

const AdGroupDeviceEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    loadingError,
    form: { deviceSettingType, deviceTypes },
    preview: {
      deviceSettingType: previewType,
      deviceTypes: previewDeviceTypes,
    },
  } = useSelector(
    ({ dashboard }) => dashboard.operation.adGroupDeviceEdit,
    shallowEqual
  )
  const { errors, validate } = useValidator(
    () => ({
      device:
        deviceSettingType === 'SETTING' &&
        isEmpty(deviceTypes) &&
        '디바이스 상세설정에 선택된 값이 없습니다.',
    }),
    [deviceSettingType, deviceTypes]
  )
  const handleCheckboxChange = ({ target: { value, checked } }) => {
    dispatch(
      setDeviceTypes({
        checked,
        value,
      })
    )
  }
  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview())
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(updateAdGroupsDevice())
        if (error) {
          preventClose()
          fail(
            '광고그룹 디바이스 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('광고그룹 디바이스 변경사항이 적용되었습니다.')
        }
      }
    } catch (ignore) {
      preventClose()
    }
  }

  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(
    () => () => {
      dispatch(hideAdGroupDeviceEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupDeviceEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const preview = previewVisible && isEmptyObject(errors)
  const changed = !deviceEquals(
    {
      type: deviceSettingType,
      deviceTypes,
    },
    {
      type: previewType,
      deviceTypes: previewDeviceTypes,
    }
  )

  return (
    <Modal on={visible} className="device_layer">
      <PreviewConfirmModal
        title="광고그룹 디바이스 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed && preview}
        handleClose={() => dispatch(hideAdGroupDeviceEdit())}>
        <p className="desc_copy">
          광고가 노출되는 모바일 또는 PC 디바이스를 설정하며, 설정된
          디바이스에만 광고가 노출됩니다.
        </p>
        {isLoading ? (
          <div className="box_subcard">
            <Loading />
          </div>
        ) : (
          <>
            <ul className="list_radiocheck">
              <li>
                <RadioButton
                  name="deviceSettingType"
                  value="ALL"
                  label="전체"
                  checked={deviceSettingType === 'ALL'}
                  onValueChange={v => dispatch(setDeviceSettingType(v))}
                />
              </li>
              <li>
                <RadioButton
                  name="deviceSettingType"
                  value="SETTING"
                  label="상세설정"
                  checked={deviceSettingType === 'SETTING'}
                  onValueChange={v => dispatch(setDeviceSettingType(v))}
                />
              </li>
            </ul>
            {deviceSettingType === 'SETTING' && (
              <div
                className={classNames('box_subcard', {
                  error: !isEmptyObject(errors),
                })}>
                <strong className="screen_out">디바이스 상세 설정</strong>
                <ul className="list_radiocheck">
                  <li>
                    <CheckBox
                      label="모바일"
                      name="deviceTypes"
                      value="MOBILE"
                      checked={deviceTypes.includes('MOBILE')}
                      onChange={handleCheckboxChange}
                    />
                  </li>
                  <li>
                    <CheckBox
                      label="PC"
                      name="deviceTypes"
                      value="PC"
                      checked={deviceTypes.includes('PC')}
                      onChange={handleCheckboxChange}
                    />
                  </li>
                </ul>
              </div>
            )}
            {errors.device && <p className="txt_error">{errors.device}</p>}
            {preview && (
              <FixedTable caption="광고그룹 디바이스 변경 이력 표">
                <Table.Head>
                  <Table.Column
                    title="선택 광고그룹"
                    className="inner_cell"
                    width={194}
                  />
                  <Table.Column
                    title="변경 전"
                    className="inner_cell"
                    width={172}
                  />
                  <Table.Column title="변경 후" className="inner_cell" />
                </Table.Head>
                <Table.Body>
                  {adGroups.map(({ id, name, deviceSetting }) => (
                    <Table.Row
                      rowId={id}
                      key={id}
                      className={classNames({
                        update: !deviceEquals(deviceSetting, {
                          type: previewType,
                          deviceTypes: previewDeviceTypes,
                        }),
                      })}>
                      <td>
                        <div className="inner_cell">
                          <span className="txt_limit2">{name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="inner_cell">
                          <DeviceName
                            type={deviceSetting.type}
                            deviceTypes={deviceSetting.deviceTypes}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="inner_cell">
                          <DeviceName
                            type={previewType}
                            deviceTypes={previewDeviceTypes}
                          />
                        </div>
                      </td>
                    </Table.Row>
                  ))}
                </Table.Body>
              </FixedTable>
            )}
          </>
        )}
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupDeviceEditModal
